(() => {
  'use strict';
  const root = document.documentElement;
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  let paused = false;
  let revealObserver;
  let activityObserver;
  let framePending = false;
  let scenes = [];
  let stories = [];
  try { paused = sessionStorage.getItem('yaman-motion-paused') === 'true'; } catch (_) {}
  const reduced = () => paused || preference.matches;
  function cache(excluded) {
    const keep = element => !excluded?.contains(element);
    scenes = Array.from(document.querySelectorAll('.sound-scene')).filter(keep).map(element => ({
      element, messages: Array.from(element.closest('.sound')?.querySelectorAll('[data-message]') || [])
    }));
    stories = Array.from(document.querySelectorAll('.scroll-story')).filter(keep).map(element => ({
      element, chapters: Array.from(element.querySelectorAll('[data-chapter]'))
    }));
  }
  function apply() {
    const off = reduced();
    root.classList.toggle('story-enabled', !off);
    root.classList.toggle('page-hidden', document.hidden);
    root.classList.toggle('motion-paused', off);
    document.querySelectorAll('[data-motion-toggle]').forEach(button => {
      button.textContent = off ? 'Motion off' : 'Pause motion';
      button.setAttribute('aria-pressed', String(off));
      button.disabled = preference.matches;
      button.title = preference.matches ? 'Your device has reduced motion enabled.' : 'Turn decorative motion on or off.';
    });
    if (off) {
      root.classList.remove('motion-ready');
      revealObserver?.disconnect();
      activityObserver?.disconnect();
      document.querySelectorAll('.motion-reveal').forEach(element => element.classList.add('is-visible'));
    }
  }
  function reveal(container = document) {
    if (reduced() || !('IntersectionObserver' in window)) return;
    if (!revealObserver) revealObserver = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }
    }, { threshold: 0.08 });
    if (!activityObserver) activityObserver = new IntersectionObserver(entries => {
      for (const entry of entries) entry.target.classList.toggle('motion-offscreen', !entry.isIntersecting);
    });
    root.classList.add('motion-ready');
    container.querySelectorAll('.section-top, .sound, .about > div, .hotlist-explore > div').forEach(element => {
      element.classList.add('motion-reveal');
      revealObserver.observe(element);
    });
    container.querySelectorAll('.orbit, .equalizer, .production-word, .playing-cover-wave').forEach(element => activityObserver.observe(element));
  }
  const clamp = value => Math.max(0, Math.min(1, value));
  function paint() {
    framePending = false;
    if (reduced() || document.hidden) return;
    const viewportHeight = innerHeight;
    const range = Math.max(1, root.scrollHeight - viewportHeight);
    const progress = clamp(scrollY / range);
    const sceneBoxes = scenes.map(scene => ({ ...scene, box: scene.element.getBoundingClientRect() }));
    const storyBoxes = stories.map(story => ({ ...story, box: story.element.getBoundingClientRect() }));
    root.style.setProperty('--scroll-hue', String(78 + progress * 205));
    root.style.setProperty('--scroll-drift', `${Math.round(progress * 70)}px`);
    for (const { element, messages, box } of sceneBoxes) {
      if (box.bottom < 0 || box.top > viewportHeight) continue;
      const amount = clamp((viewportHeight - box.top) / (viewportHeight + box.height));
      element.style.setProperty('--card-progress', String(amount));
      const messageProgress = clamp((amount - 0.12) / 0.76);
      for (const message of messages) {
        const distance = Math.abs(messageProgress * 2 - Number(message.dataset.message));
        message.style.setProperty('--message-alpha', String(Math.max(0, 1 - distance * 1.6)));
        message.style.setProperty('--message-y', `${Math.min(20, distance * 20)}px`);
        message.style.setProperty('--message-blur', `${Math.min(3, distance * 2)}px`);
      }
    }
    for (const { element, chapters, box } of storyBoxes) {
      if (box.bottom < 0 || box.top > viewportHeight) continue;
      const local = clamp(-box.top / Math.max(1, box.height - viewportHeight));
      element.style.setProperty('--scene-progress', String(local));
      element.style.setProperty('--scene-hue', String(78 + local * 205));
      for (const chapter of chapters) {
        const distance = Math.abs(local * 2 - Number(chapter.dataset.chapter));
        chapter.style.setProperty('--chapter-opacity', String(Math.max(0, 1 - distance * 1.8)));
        chapter.style.setProperty('--chapter-offset', `${Math.min(22, distance * 22)}px`);
      }
    }
  }
  function schedule() {
    if (framePending || reduced() || document.hidden) return;
    framePending = true;
    requestAnimationFrame(paint);
  }
  function setup() {
    cache();
    apply();
    reveal();
    schedule();
    document.querySelectorAll('input[name="contact[Event date]"]').forEach(input => {
      const now = new Date();
      const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      input.min = localDate;
    });
  }
  document.addEventListener('click', event => {
    const button = event.target instanceof Element ? event.target.closest('[data-motion-toggle]') : null;
    if (!button || preference.matches) return;
    paused = !paused;
    try { sessionStorage.setItem('yaman-motion-paused', String(paused)); } catch (_) {}
    apply();
    if (!paused) { reveal(); schedule(); }
  });
  preference.addEventListener?.('change', setup);
  document.addEventListener('shopify:section:load', setup);
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.motion-reveal').forEach(element => revealObserver?.unobserve(element));
    event.target.querySelectorAll('.orbit, .equalizer, .production-word, .playing-cover-wave').forEach(element => activityObserver?.unobserve(element));
    cache(event.target);
    schedule();
  });
  document.addEventListener('visibilitychange', () => {
    root.classList.toggle('page-hidden', document.hidden);
    if (!document.hidden) schedule();
  });
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  setup();
})();

